/**
 * @tcc/shared — shared TypeScript domain types for the AI Token Cost Calculator.
 *
 * This module is the single import surface consumed by both the server
 * (@tcc/server) and the client (@tcc/client). The shapes mirror the "Data
 * Models" section of the approved design document.
 */
/** Default parameter set (calibrated to the spreadsheet tokeniser assumptions). */
export const DEFAULT_PARAMS = {
    // Tokeniser assumptions §1 Text sizing (input). Page recalibrated to 3,000
    // chars ÷ 4 = 750 tokens, document to 3,000 tokens (= 4 pages). Diverges from
    // the v5 defaults of 650 / 2,600.
    textInputTokens: { sentence: 25, paragraph: 130, page: 750, document: 3000 },
    // v4 §2 Modality conversion
    tokensPerVoiceMinute: 1920,
    tokensPerVideoMinute: 17400,
    bytesPerRagToken: 4, // §1 chars per token
    systemPromptBaselineTokens: 0,
    // v4 §4 Output sizing (page 750, document 3,000 tokens to match text input).
    outputSizeBaseTokens: { sentence: 25, paragraph: 130, page: 750, document: 3000 },
    // Wizard step 3 sizing-slider bounds, per content type
    sizingRanges: {
        textPages: { min: 1, max: 100 },
        voiceMinutes: { min: 1, max: 60 },
        imageCount: { min: 1, max: 20 },
        videoMinutes: { min: 1, max: 30 },
    },
    // Decision-complexity permutations (per decision): Simple 10 / Complex 100
    sensitivityPermutations: { simple: 10, complex: 100 },
    // v4 §5 Decision overhead — per-permutation token costs
    perDecisionTokenCost: 130, // Decision outcome
    chainOfThoughtPerDecision: 130, // Chain of Thought (CoT)
    rationalePerDecision: 130, // Rationale for decision
    fxRateUsdToGbp: 0.8,
    // Volumetrics basis: calendar-average month (365/12 ≈ 30.42 days) and full year.
    daysPerMonth: 365 / 12,
    daysPerYear: 365,
    // ── B. PT Sizing (v4 PT Sizing tab) ────────────────────────────────────
    ptSizing: {
        gemini25Flash: { perSecondThroughputPerGsu: 100 },
        gemini25FlashLite: { perSecondThroughputPerGsu: 200 },
        gemini25Pro: { perSecondThroughputPerGsu: 20 },
        gemini31FlashLite: { perSecondThroughputPerGsu: 100 },
        gemini31Pro: { perSecondThroughputPerGsu: 10 },
    },
    ptCostPerGsuPerMonth: 2000, // USD per GSU per month
    // ── C. Runtime Assumptions (v4 Runtime assumptions tab) ─────────────────
    runtimeAssumptions: {
        simpleChatbot: { vcpuPerRps: 0.05, gibRamPerRps: 0.1 },
        standardAgentic: { vcpuPerRps: 0.1, gibRamPerRps: 0.2 },
        complexMultiAgent: { vcpuPerRps: 0.2, gibRamPerRps: 0.4 },
        heavyToolUse: { vcpuPerRps: 0.3, gibRamPerRps: 0.5 },
    },
    runtimeVcpuRateUsdPerHour: 0.0864, // free tier: 50 vCPU-hr/mo
    runtimeRamRateUsdPerGibHour: 0.009, // free tier: 100 GiB-hr/mo
    // ── D. Agent Runtime rates (v4 Pricing tab B) ───────────────────────────
    agentSessionsRateUsd: 0.25, // per 1,000 events stored
    agentMemoryStoredRateUsd: 0.25, // per 1,000 memories/mo
    agentMemoryRetrievedRateUsd: 0.5, // per 1,000 memories returned
    // ── E. Add-ons pricing (v4 Pricing tab C) ───────────────────────────────
    modelArmorUsdPer1MTokens: 0.1,
    apigeeBaseFeeGbp: 365,
    apigeeVariableRateGbpPer1MApiCalls: 20,
};
export { DEFAULT_DAYS_PER_MONTH, DEFAULT_DAYS_PER_YEAR, deriveVolumetrics, MONTHS_PER_YEAR, WORKING_DAYS_PER_MONTH, } from './volumetrics.js';
//# sourceMappingURL=index.js.map